import { BusinessDate } from '../date/business-date';

/**
 * Conversão do que vem da planilha.
 *
 * Duas regras governam este arquivo:
 *
 * - **data nunca passa por `new Date()`** (RN-120). Construir um `Date` a partir de
 *   "01/09/2026" aplicaria o fuso do dispositivo e poderia gravar 31/08 para quem está a
 *   oeste de Greenwich. Aqui o texto vira `BusinessDate` por aritmética de calendário;
 * - **número respeita a escrita brasileira**: "1.250,75" é mil duzentos e cinquenta, não
 *   um e vinte e cinco. Ler isso errado vira ordem de compra errada.
 */

export type ParseFailure = 'EMPTY' | 'FORMAT';

export interface ParsedValue<T> {
  readonly ok: boolean;
  readonly value?: T;
  readonly failure?: ParseFailure;
}

const ok = <T>(value: T): ParsedValue<T> => ({ ok: true, value });
const fail = <T>(failure: ParseFailure): ParsedValue<T> => ({ ok: false, failure });

/**
 * Serial de data do Excel.
 *
 * O Excel conta dias desde 30/12/1899 — o 1900 bissexto que nunca existiu já está
 * embutido nessa âncora. Uma planilha salva com a coluna formatada como data entrega
 * `45900`, não `01/09/2026`, e é isso que chega ao leitor.
 */
const EXCEL_EPOCH_DAYS = 25_569; // dias entre 30/12/1899 e 01/01/1970
const MIN_SERIAL = 20_000; // ~1954 — abaixo disso é número solto, não data
const MAX_SERIAL = 80_000; // ~2119

function fromExcelSerial(serial: number): ParsedValue<string> {
  const days = Math.trunc(serial);
  if (days < MIN_SERIAL || days > MAX_SERIAL) return fail('FORMAT');

  // Aritmética em UTC puro: nenhum fuso participa da conta.
  const millis = (days - EXCEL_EPOCH_DAYS) * 86_400_000;
  const date = new Date(millis);
  const iso = `${String(date.getUTCFullYear()).padStart(4, '0')}-${String(
    date.getUTCMonth() + 1,
  ).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

  const parsed = BusinessDate.parse(iso);
  return parsed.ok ? ok(parsed.value.toString()) : fail('FORMAT');
}

function fromParts(day: number, month: number, year: number): ParsedValue<string> {
  const fullYear = year < 100 ? 2000 + year : year;
  const parsed = BusinessDate.fromParts(fullYear, month, day);
  return parsed.ok ? ok(parsed.value.toString()) : fail('FORMAT');
}

/**
 * Data da planilha → `YYYY-MM-DD`.
 *
 * Aceita `dd/MM/yyyy`, `dd-MM-yyyy`, `dd.MM.yyyy`, `yyyy-MM-dd`, `dd/MM/yy` e o serial do
 * Excel. Recusa o resto: adivinhar entre `03/04` e `04/03` produziria um cronograma
 * plausível e errado, que é pior do que um erro visível.
 */
export function parseImportDate(raw: string): ParsedValue<string> {
  const value = raw.trim();
  if (value === '') return fail('EMPTY');

  // ISO primeiro: é o formato que o próprio sistema exporta.
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
  if (iso !== null) {
    return fromParts(Number(iso[3]), Number(iso[2]), Number(iso[1]));
  }

  const brazilian = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/.exec(value);
  if (brazilian !== null) {
    return fromParts(Number(brazilian[1]), Number(brazilian[2]), Number(brazilian[3]));
  }

  // Serial do Excel — só quando o texto é exclusivamente numérico.
  if (/^\d+([.,]\d+)?$/.test(value)) {
    return fromExcelSerial(Number(value.replace(',', '.')));
  }

  return fail('FORMAT');
}

/**
 * Quantidade da planilha → número.
 *
 * O caso ambíguo é o ponto: "1.250" é mil duzentos e cinquenta no Brasil e um vírgula
 * dois cinco em inglês. A vírgula desempata — quando ela existe, o ponto é separador de
 * milhar; quando não existe, um ponto com exatamente três dígitos depois também é milhar
 * ("1.250"), e qualquer outra contagem é decimal ("1.25").
 */
export function parseImportQuantity(raw: string): ParsedValue<number> {
  const value = raw
    .trim()
    .replace(/\s/g, '')
    .replace(/[^\d.,-]/g, '');
  if (value === '') return fail('EMPTY');

  let normalized: string;

  if (value.includes(',')) {
    normalized = value.replace(/\./g, '').replace(',', '.');
  } else {
    const groups = value.split('.');
    const isThousands = groups.length > 1 && groups.slice(1).every((group) => group.length === 3);
    normalized = isThousands ? groups.join('') : value;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? ok(parsed) : fail('FORMAT');
}
